import { useState, memo, useMemo } from 'react';
import { Box, Typography, Paper, Button, TextField, Stack, Chip } from '@mui/material';
import type { Board, SquareRequest } from '../../types';
import { StatusAlert } from './StatusAlert';
import { useNumberInput } from '../../hooks';

export interface SquareRequestBannerProps {
  requests: SquareRequest[];
  board: Board;
  onSubmitRequest: (requestedSquares: number) => Promise<void>;
  onCancelRequest: (requestId: string) => Promise<void>;
  userClaimedCount: number;
}

export const SquareRequestBanner = memo<SquareRequestBannerProps>(({
  requests,
  board,
  onSubmitRequest,
  onCancelRequest,
  userClaimedCount,
}) => {
  const [requestCount, setRequestCount] = useState(5);
  const [submitting, setSubmitting] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  // Number input hook for better typing UX
  const requestCountInput = useNumberInput(requestCount, setRequestCount, { min: 1, max: 100, defaultValue: 1, allowDecimals: false });

  const MAX_ACTIVE_REQUESTS = 5;

  // Compute aggregated request state
  const { pendingRequests, approvedRequests, deniedRequests, totalApproved, totalPending, remainingRequests } = useMemo(() => {
    const pending = requests.filter(r => r.status === 'pending');
    const approved = requests.filter(r => r.status === 'approved');
    const denied = requests.filter(r => r.status === 'denied');
    const activeCount = pending.length + approved.length;
    return {
      pendingRequests: pending,
      approvedRequests: approved,
      deniedRequests: denied,
      totalApproved: approved.reduce((sum, r) => sum + (r.approvedSquares || 0), 0),
      totalPending: pending.reduce((sum, r) => sum + r.requestedSquares, 0),
      remainingRequests: MAX_ACTIVE_REQUESTS - activeCount,
    };
  }, [requests]);

  const canSubmitMoreRequests = remainingRequests > 0;

  // Helper to get the remaining requests message
  const getRemainingRequestsMessage = () => {
    if (remainingRequests === 1) {
      return 'This is your last available request.';
    }
    if (remainingRequests > 1) {
      return `You can submit ${remainingRequests} more request${remainingRequests !== 1 ? 's' : ''}.`;
    }
    return null;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmitRequest(requestCount);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (requestId: string) => {
    setCancellingId(requestId);
    try {
      await onCancelRequest(requestId);
    } finally {
      setCancellingId(null);
    }
  };

  // If board is completed, show completed message
  if (board.status === 'completed') {
    const totalCost = userClaimedCount * board.pricePerSquare;
    return (
      <StatusAlert
        severity="info"
        title="Game Complete!"
        body={`You had ${userClaimedCount} square${userClaimedCount !== 1 ? 's' : ''} ($${totalCost.toFixed(2)} total). Check the Players tab for your payment status and any winnings.`}
      />
    );
  }

  // If there are approved requests, show what they can claim
  if (approvedRequests.length > 0) {
    const remaining = totalApproved - userClaimedCount;
    const allClaimed = remaining <= 0;

    // If board is in draft status, show a note that they can pick squares when published
    if (board.status === 'draft') {
      return (
        <Stack spacing={2}>
          <StatusAlert
            severity="info"
            title={`You have ${totalApproved} approved square${totalApproved !== 1 ? 's' : ''}!`}
            body="You'll be able to pick your squares once the board is published. The board manager is still setting things up."
          />
          {pendingRequests.length > 0 && (
            <StatusAlert
              severity="info"
              title={`${totalPending} additional square${totalPending !== 1 ? 's' : ''} pending approval`}
              body={
                <Box>
                  {pendingRequests.map(req => (
                    <Box key={req.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Chip size="small" label={`${req.requestedSquares} squares`} />
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={() => handleCancel(req.id)}
                        disabled={cancellingId === req.id}
                      >
                        Cancel
                      </Button>
                    </Box>
                  ))}
                </Box>
              }
            />
          )}
          {/* Allow requesting more even on draft boards */}
          {canSubmitMoreRequests ? (
            <Paper sx={{ p: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Request More Squares
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Want more squares? Submit another request for approval.
                {getRemainingRequestsMessage() && (
                  <Typography component="span" variant="body2" color={remainingRequests === 1 ? 'warning.main' : 'text.secondary'} sx={{ ml: 1 }}>
                    {getRemainingRequestsMessage()}
                  </Typography>
                )}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  type="number"
                  size="small"
                  label="Number of Squares"
                  value={requestCountInput.value}
                  onChange={requestCountInput.onChange}
                  onBlur={requestCountInput.onBlur}
                  inputProps={{ min: 1, max: 100 }}
                  sx={{ width: 150 }}
                />
                <Typography variant="body2" color="text.secondary">
                  = ${(requestCount * board.pricePerSquare).toFixed(2)} total
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting || requestCount < 1 || requestCount > 100}
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </Box>
            </Paper>
          ) : (
            <StatusAlert
              severity="info"
              title="Request limit reached"
              body="You've reached the maximum of 5 active requests. Cancel a pending request to submit a new one."
            />
          )}
        </Stack>
      );
    }

    // Board is published - show claiming status
    return (
      <Stack spacing={2}>
        <StatusAlert
          severity={allClaimed ? 'info' : 'success'}
          title={allClaimed
            ? `You've claimed all ${totalApproved} of your approved squares!`
            : `You've claimed ${userClaimedCount} of ${totalApproved} approved squares`
          }
          body={allClaimed
            ? 'Click on your squares (blue) to unclaim them if needed. You can also request more squares below.'
            : `Click on any available (white) square to claim it, or click your squares (blue) to unclaim. Each square costs $${board.pricePerSquare.toFixed(2)}.`
          }
        />
        {pendingRequests.length > 0 && (
          <StatusAlert
            severity="info"
            title={`${totalPending} additional square${totalPending !== 1 ? 's' : ''} pending approval`}
            body={
              <Box>
                {pendingRequests.map(req => (
                  <Box key={req.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <Chip size="small" label={`${req.requestedSquares} squares`} />
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={() => handleCancel(req.id)}
                      disabled={cancellingId === req.id}
                    >
                      Cancel
                    </Button>
                  </Box>
                ))}
              </Box>
            }
          />
        )}
        {/* Show request more form - conditionally based on limit */}
        {canSubmitMoreRequests ? (
          <Paper sx={{ p: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Request More Squares
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Want more squares? Submit another request for approval.
              {getRemainingRequestsMessage() && (
                <Typography component="span" variant="body2" color={remainingRequests === 1 ? 'warning.main' : 'text.secondary'} sx={{ ml: 1 }}>
                  {getRemainingRequestsMessage()}
                </Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                type="number"
                size="small"
                label="Number of Squares"
                value={requestCountInput.value}
                onChange={requestCountInput.onChange}
                onBlur={requestCountInput.onBlur}
                inputProps={{ min: 1, max: 100 }}
                sx={{ width: 150 }}
              />
              <Typography variant="body2" color="text.secondary">
                = ${(requestCount * board.pricePerSquare).toFixed(2)} total
              </Typography>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || requestCount < 1 || requestCount > 100}
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </Box>
          </Paper>
        ) : (
          <StatusAlert
            severity="info"
            title="Request limit reached"
            body="You've reached the maximum of 5 active requests. Cancel a pending request to submit a new one."
          />
        )}
      </Stack>
    );
  }

  // If only pending requests exist, show pending status
  if (pendingRequests.length > 0) {
    return (
      <Stack spacing={2}>
        <StatusAlert
          severity="info"
          title={`Your request${pendingRequests.length > 1 ? 's' : ''} for ${totalPending} square${totalPending !== 1 ? 's' : ''} ${pendingRequests.length > 1 ? 'are' : 'is'} pending approval`}
          body={
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                A board manager will review your request. You'll be able to claim squares once approved.
              </Typography>
              {pendingRequests.map(req => (
                <Box key={req.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Chip size="small" label={`${req.requestedSquares} squares`} />
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={() => handleCancel(req.id)}
                    disabled={cancellingId === req.id}
                  >
                    Cancel
                  </Button>
                </Box>
              ))}
            </Box>
          }
        />
        {/* Allow requesting more even while pending - if under limit */}
        {canSubmitMoreRequests ? (
          <Paper sx={{ p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Request Additional Squares
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Want more? Submit another request. Each request is reviewed separately.
              {getRemainingRequestsMessage() && (
                <Typography component="span" variant="body2" color={remainingRequests === 1 ? 'warning.main' : 'text.secondary'} sx={{ ml: 1 }}>
                  {getRemainingRequestsMessage()}
                </Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                type="number"
                size="small"
                label="Number of Squares"
                value={requestCountInput.value}
                onChange={requestCountInput.onChange}
                onBlur={requestCountInput.onBlur}
                inputProps={{ min: 1, max: 100 }}
                sx={{ width: 150 }}
              />
              <Typography variant="body2" color="text.secondary">
                = ${(requestCount * board.pricePerSquare).toFixed(2)} total
              </Typography>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting || requestCount < 1 || requestCount > 100}
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </Box>
          </Paper>
        ) : (
          <StatusAlert
            severity="info"
            title="Request limit reached"
            body="You've reached the maximum of 5 active requests. Cancel a pending request above to submit a new one."
          />
        )}
      </Stack>
    );
  }

  // Check for recent denials to show warning
  const recentDenial = deniedRequests.length > 0 ? deniedRequests[0] : null;

  // No active requests - show request form
  return (
    <Stack spacing={2}>
      {recentDenial && (
        <StatusAlert
          severity="warning"
          title="Your previous request was denied"
          body={
            <>
              {recentDenial.denialReason && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Reason: {recentDenial.denialReason}
                </Typography>
              )}
              <Typography variant="body2">
                You can submit a new request below or contact a board manager if you have questions.
              </Typography>
            </>
          }
        />
      )}
      {canSubmitMoreRequests ? (
        <Paper sx={{ p: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Request Squares
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Before you can claim squares, you need to request how many you want. A manager will review and approve your request.
            {getRemainingRequestsMessage() && remainingRequests < MAX_ACTIVE_REQUESTS && (
              <Typography component="span" variant="body2" color={remainingRequests === 1 ? 'warning.main' : 'text.secondary'} sx={{ ml: 1 }}>
                {getRemainingRequestsMessage()}
              </Typography>
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              type="number"
              size="small"
              label="Number of Squares"
              value={requestCountInput.value}
              onChange={requestCountInput.onChange}
              onBlur={requestCountInput.onBlur}
              inputProps={{ min: 1, max: 100 }}
              sx={{ width: 150 }}
            />
            <Typography variant="body2" color="text.secondary">
              = ${(requestCount * board.pricePerSquare).toFixed(2)} total
            </Typography>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || requestCount < 1 || requestCount > 100}
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </Box>
        </Paper>
      ) : (
        <StatusAlert
          severity="info"
          title="Request limit reached"
          body="You've reached the maximum of 5 active requests."
        />
      )}
    </Stack>
  );
});

SquareRequestBanner.displayName = 'SquareRequestBanner';

export default SquareRequestBanner;
